import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, setMonth, setYear, getYear, getMonth } from 'date-fns';

const EventsTimeFilter = ({
  onTimeFilterChange,
  selectedPreset = 'today',
  openCustomOnMount = false,
  hidePresetButtons = false,
  onClose = null,
}) => {
  const [isCustomOpen, setIsCustomOpen] = useState(openCustomOnMount);
  const [customMode, setCustomMode] = useState('fixed'); // 'fixed', 'last', 'since'
  const [enableTimeRanges, setEnableTimeRanges] = useState(false);
  const [activePreset, setActivePreset] = useState(selectedPreset);
  
  // Fixed mode state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  
  // Last mode state
  const [lastAmount, setLastAmount] = useState(1);
  const [lastUnit, setLastUnit] = useState('days');
  const [endingAmount, setEndingAmount] = useState(0);
  const [endingUnit, setEndingUnit] = useState('days');
  
  // Since mode state
  const [sinceDate, setSinceDate] = useState('');
  const [sinceTime, setSinceTime] = useState('00:00');

  // Calendar UI state (for fixed/since modes) - show 2 months side by side
  const [calendarMonth1, setCalendarMonth1] = useState(() => startOfMonth(new Date()));
  const [calendarMonth2, setCalendarMonth2] = useState(() => startOfMonth(addMonths(new Date(), 1)));
  
  const dropdownRef = useRef(null);
  const customDropdownRef = useRef(null);

  const presets = [
    { label: 'Custom', value: 'custom' },
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '3M', value: '3m' },
    { label: '6M', value: '6m' },
    { label: '12M', value: '12m' }
  ];

  const timeUnits = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'quarters', label: 'Quarters' }
  ];

  const MODE_ACTIVE_COLOR = '#4B57AA';

  const parseYmd = (ymd) => {
    if (!ymd) return null;
    // ymd is YYYY-MM-DD; this parses in local time reliably for our UI
    const d = new Date(ymd);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toYmd = (d) => {
    if (!d) return '';
    try {
      return format(d, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  };

  // Generate month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: format(new Date(2024, i, 1), 'MMMM')
    }));
  }, []);

  // Generate year options (current year ± 10 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  }, []);

  const CalendarGrid = ({ mode, month, onMonthChange }) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstWeekday = monthStart.getDay(); // 0=Sun

    const startSelected = mode === 'fixed' ? parseYmd(startDate) : parseYmd(sinceDate);
    const endSelected = mode === 'fixed' ? parseYmd(endDate) : null;

    const isInRange = (day) => {
      if (mode !== 'fixed') return false;
      if (!startSelected || !endSelected) return false;
      const a = startSelected <= endSelected ? startSelected : endSelected;
      const b = startSelected <= endSelected ? endSelected : startSelected;
      return day >= a && day <= b;
    };

    const handleDayClick = (day) => {
      const ymd = toYmd(day);
      if (!ymd) return;

      if (mode === 'since') {
        setSinceDate(ymd);
        return;
      }

      // fixed range selection
      if (!startDate || (startDate && endDate)) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      // start selected, pick end
      const start = parseYmd(startDate);
      if (!start) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      // If user clicks a date before start, treat it as new start.
      if (day < start) {
        setStartDate(ymd);
        setEndDate('');
        return;
      }

      setEndDate(ymd);
    };

    const weekdayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    // Convert JS Sun=0..Sat=6 into Monday-first layout
    const leadingEmpty = (firstWeekday + 6) % 7;

    const handleMonthChange = (newMonth) => {
      const newDate = setMonth(month, newMonth);
      onMonthChange(newDate);
    };

    const handleYearChange = (newYear) => {
      const newDate = setYear(month, newYear);
      onMonthChange(newDate);
    };

    return (
      <div className="select-none">
        <div className="flex items-center justify-between mb-1.5">
          <button
            type="button"
            onClick={() => onMonthChange(subMonths(month, 1))}
            className="p-1 rounded-lg hover:bg-gray-100 text-[#475569]"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <select
              value={getMonth(month)}
              onChange={(e) => handleMonthChange(parseInt(e.target.value))}
              className="text-sm font-medium text-[#0f172a] bg-transparent border-none outline-none cursor-pointer"
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={getYear(month)}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
              className="text-sm font-medium text-[#0f172a] bg-transparent border-none outline-none cursor-pointer"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="p-1 rounded-lg hover:bg-gray-100 text-[#475569]"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 text-xs text-[#475569] mb-1">
          {weekdayLabels.map((w) => (
            <div key={w} className="text-center py-0.5">{w}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: leadingEmpty }).map((_, idx) => (
            <div key={`empty-${idx}`} className="h-7" />
          ))}

          {days.map((day) => {
            const isStart = mode === 'fixed' && startSelected && isSameDay(day, startSelected);
            const isEnd = mode === 'fixed' && endSelected && isSameDay(day, endSelected);
            const isSingle = mode === 'since' && startSelected && isSameDay(day, startSelected);
            const inRange = isInRange(day);

            const base =
              'h-7 w-7 flex items-center justify-center text-sm rounded-lg transition-colors';
            const selected =
              isStart || isEnd || isSingle ? 'bg-gray-100 text-[#0f172a]' : 'text-[#475569] hover:bg-gray-50';
            const rangeBg = inRange && !(isStart || isEnd) ? 'bg-gray-50' : '';

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDayClick(day)}
                className={`${base} ${rangeBg} ${selected}`}
                aria-label={format(day, 'yyyy-MM-dd')}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Sync second month when first month changes (always one month ahead)
  useEffect(() => {
    const expectedSecond = startOfMonth(addMonths(calendarMonth1, 1));
    if (expectedSecond.getTime() !== calendarMonth2.getTime()) {
      setCalendarMonth2(expectedSecond);
    }
  }, [calendarMonth1]);

  // Sync first month when second month changes (always one month behind)
  // Only sync if second month is not already one month ahead of first
  useEffect(() => {
    const expectedFirst = startOfMonth(subMonths(calendarMonth2, 1));
    if (expectedFirst.getTime() !== calendarMonth1.getTime()) {
      setCalendarMonth1(expectedFirst);
    }
  }, [calendarMonth2]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customDropdownRef.current && !customDropdownRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCustomOpen(false);
        if (onClose) onClose();
      }
    };

    if (isCustomOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCustomOpen, onClose]);

  // Keep active preset in sync with parent state
  useEffect(() => {
    setActivePreset(selectedPreset);
  }, [selectedPreset]);

  // Calculate date range for presets
  const getPresetDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return {
          from: new Date(today.getTime()),
          to: new Date(now.getTime())
        };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return {
          from: yesterday,
          to: yesterdayEnd
        };
      case '7d':
        return {
          from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '30d':
        return {
          from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '3m':
        return {
          from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '6m':
        return {
          from: new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000),
          to: now
        };
      case '12m':
        return {
          from: new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000),
          to: now
        };
      default:
        return null;
    }
  };

  // Convert date and time to ISO string
  const combineDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(':');
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date;
  };

  // Apply preset
  const handlePresetSelect = (preset) => {
    if (preset === 'custom') {
      setIsCustomOpen(true);
      setActivePreset('custom');
      return;
    }

    setIsCustomOpen(false);
    setActivePreset(preset);
    const range = getPresetDateRange(preset);
    if (range) {
      onTimeFilterChange({
        type: 'preset',
        preset,
        from: range.from.toISOString(),
        to: range.to.toISOString()
      });
    }
  };

  // Apply custom filter
  const handleCustomApply = () => {
    if (customMode === 'fixed') {
      if (!startDate || !endDate) return;
      
      let fromDate, toDate;
      
      if (enableTimeRanges) {
        fromDate = combineDateTime(startDate, startTime);
        toDate = combineDateTime(endDate, endTime);
      } else {
        // Default: full day inclusion
        fromDate = combineDateTime(startDate, '00:00');
        toDate = combineDateTime(endDate, '23:59');
      }
      
      if (fromDate && toDate) {
        setActivePreset('custom');
        onTimeFilterChange({
          type: 'custom',
          mode: 'fixed',
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          enableTimeRanges
        });
        setIsCustomOpen(false);
        if (onClose) onClose();
      }
    } else if (customMode === 'last') {
      // Calculate "last X units ending Y units ago"
      const now = new Date();
      const endingOffset = calculateOffset(endingAmount, endingUnit);
      const windowSize = calculateOffset(lastAmount, lastUnit);
      
      const endDate = new Date(now.getTime() - endingOffset);
      const startDate = new Date(endDate.getTime() - windowSize);
      
      setActivePreset('custom');
      onTimeFilterChange({
        type: 'custom',
        mode: 'last',
        from: startDate.toISOString(),
        to: endDate.toISOString(),
        lastAmount,
        lastUnit,
        endingAmount,
        endingUnit
      });
      setIsCustomOpen(false);
      if (onClose) onClose();
    } else if (customMode === 'since') {
      if (!sinceDate) return;
      
      const now = new Date();
      let fromDate;
      
      if (enableTimeRanges) {
        fromDate = combineDateTime(sinceDate, sinceTime);
      } else {
        fromDate = combineDateTime(sinceDate, '00:00');
      }
      
      // End is always today at 23:59 (or now if time ranges enabled)
      const toDate = enableTimeRanges ? now : combineDateTime(
        now.toISOString().split('T')[0],
        '23:59'
      );
      
      if (fromDate) {
        setActivePreset('custom');
        onTimeFilterChange({
          type: 'custom',
          mode: 'since',
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
          enableTimeRanges
        });
        setIsCustomOpen(false);
        if (onClose) onClose();
      }
    }
  };

  // Calculate offset in milliseconds
  const calculateOffset = (amount, unit) => {
    const multipliers = {
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
      weeks: 7 * 24 * 60 * 60 * 1000,
      months: 30 * 24 * 60 * 60 * 1000,
      quarters: 90 * 24 * 60 * 60 * 1000
    };
    return amount * (multipliers[unit] || multipliers.days);
  };

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    return date.toISOString().split('T')[0];
  };

  const getPresetLabel = () => {
    const preset = presets.find(p => p.value === selectedPreset);
    return preset ? preset.label : 'Custom';
  };

  const showCustomPanel = isCustomOpen || (hidePresetButtons && openCustomOnMount);

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      {/* Preset buttons - connected style (hidden when used as board custom popover) */}
      {!hidePresetButtons && (
      <div className="flex items-center">
        {presets.map((preset, index) => (
          <button
            key={preset.value}
            onClick={() => handlePresetSelect(preset.value)}
            className={`time-selector-normal px-3 py-1 text-sm font-medium transition-colors ${
              index === 0 ? 'rounded-l-lg' : ''
            } ${
              index === presets.length - 1 ? 'rounded-r-lg' : ''
            } ${
              activePreset === preset.value
                ? 'bg-gray-100 text-[#0f172a] border border-gray-300 z-10'
                : 'bg-white text-[#475569] hover:bg-gray-50 border border-gray-300'
            } ${
              index > 0 ? '-ml-px' : ''
            }`}
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            {preset.value === 'custom' && <Icon name="calendar" size={18} color="currentColor" style={{ display: 'inline', marginRight: '4px' }} />}
            {preset.label}
          </button>
        ))}
      </div>
      )}

      {/* Custom time filter popup */}
      {showCustomPanel && (
        <div
          ref={customDropdownRef}
          className="absolute top-full left-0 mt-2 w-[750px] bg-white border border-gray-200 shadow-xl rounded-lg z-50 overflow-hidden"
          style={{ fontFamily: 'Raleway, sans-serif' }}
        >
          <div className="flex">
            {/* Left panel - Mode selection */}
            <div className="w-28 border-r border-gray-200 bg-gray-50 rounded-l-lg">
              <div className="p-1.5 space-y-0.5">
                <button
                  type="button"
                  onClick={() => setCustomMode('fixed')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'fixed' ? MODE_ACTIVE_COLOR : '#0f172a',
                    fontWeight: customMode === 'fixed' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Fixed
                </button>
                <button
                  type="button"
                  onClick={() => setCustomMode('last')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'last' ? MODE_ACTIVE_COLOR : '#0f172a',
                    fontWeight: customMode === 'last' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Last
                </button>
                <button
                  type="button"
                  onClick={() => setCustomMode('since')}
                  className="w-full text-left px-3 py-2 text-sm transition-colors"
                  style={{
                    color: customMode === 'since' ? MODE_ACTIVE_COLOR : '#0f172a',
                    fontWeight: customMode === 'since' ? 600 : 500,
                    background: 'transparent'
                  }}
                >
                  Since
                </button>
              </div>
            </div>

            {/* Right panel - Configuration */}
            <div className="flex-1 p-3">
              {/* Fixed mode */}
              {customMode === 'fixed' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="text-xs text-[#475569] mb-1">Starts</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-[#0f172a]">
                          {startDate || 'Select start date'}
                        </div>
                        {enableTimeRanges && (
                          <input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm text-[#0f172a] w-24"
                          />
                        )}
                      </div>
                    </div>
                    <div className="text-[#475569] text-lg pt-5">→</div>
                    <div className="flex-1">
                      <div className="text-xs text-[#475569] mb-1">Ends</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-[#0f172a]">
                          {endDate || 'Select end date'}
                        </div>
                        {enableTimeRanges && (
                          <input
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm text-[#0f172a] w-24"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inline calendar (range) - 2 months side by side */}
                  <div className="pt-1">
                    <div className="grid grid-cols-2 gap-3">
                      <CalendarGrid mode="fixed" month={calendarMonth1} onMonthChange={setCalendarMonth1} />
                      <CalendarGrid mode="fixed" month={calendarMonth2} onMonthChange={setCalendarMonth2} />
                    </div>
                    <div className="text-xs text-[#475569] mt-1.5">
                      Click a start date, then an end date.
                    </div>
                  </div>
                </div>
              )}

              {/* Last mode */}
              {customMode === 'last' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[#475569] mb-1.5">
                      Show me last
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={lastAmount}
                        onChange={(e) => setLastAmount(parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-20 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <select
                        value={lastUnit}
                        onChange={(e) => {
                          setLastUnit(e.target.value);
                          setEndingUnit(e.target.value); // Keep units in sync
                        }}
                        className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        {timeUnits.map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#475569] mb-1.5">
                      Ending
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={endingAmount}
                        onChange={(e) => setEndingAmount(parseInt(e.target.value) || 0)}
                        min="0"
                        className="w-20 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <select
                        value={endingUnit}
                        onChange={(e) => {
                          setEndingUnit(e.target.value);
                          setLastUnit(e.target.value); // Keep units in sync
                        }}
                        className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        {timeUnits.map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-[#475569]">ago</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Since mode */}
              {customMode === 'since' && (
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-[#475569] mb-1">From date</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-[#0f172a]">
                        {sinceDate || 'Select start date'}
                      </div>
                      {enableTimeRanges && (
                        <input
                          type="time"
                          value={sinceTime}
                          onChange={(e) => setSinceTime(e.target.value)}
                          className="px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-sm text-[#0f172a] w-24"
                        />
                      )}
                    </div>
                    <p className="text-xs text-[#475569] mt-1">
                      End date: {new Date().toLocaleDateString()} (today)
                    </p>
                  </div>

                  {/* Inline calendar (single) - 2 months side by side */}
                  <div className="pt-1">
                    <div className="grid grid-cols-2 gap-3">
                      <CalendarGrid mode="since" month={calendarMonth1} onMonthChange={setCalendarMonth1} />
                      <CalendarGrid mode="since" month={calendarMonth2} onMonthChange={setCalendarMonth2} />
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons and Enable Time Ranges toggle */}
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setEnableTimeRanges(!enableTimeRanges)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-2 border border-gray-300 ${
                    enableTimeRanges
                      ? 'bg-[#4b57aa] text-white'
                      : 'bg-white text-[#475569] hover:bg-gray-50'
                  }`}
                >
                  <span>Enable Time Ranges</span>
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsCustomOpen(false);
                      if (onClose) onClose();
                    }}
                    className="px-3 py-1.5 text-sm text-[#475569] hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCustomApply}
                    disabled={
                      (customMode === 'fixed' && (!startDate || !endDate)) ||
                      (customMode === 'since' && !sinceDate) ||
                      (customMode === 'last' && (!lastAmount || lastAmount <= 0))
                    }
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors border border-gray-300 ${
                      (customMode === 'fixed' && (!startDate || !endDate)) ||
                      (customMode === 'since' && !sinceDate) ||
                      (customMode === 'last' && (!lastAmount || lastAmount <= 0))
                        ? 'bg-gray-100 text-[#475569] cursor-not-allowed'
                        : 'bg-[#4b57aa] text-white hover:bg-[#3d4799]'
                    }`}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTimeFilter;
